import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import UserManagement from './UserManagement';
import PGNDemoAdmin from './PGNDemoAdmin';
import AIKnowledgeAdmin from './AIKnowledgeAdmin';
import styled from 'styled-components';
import { theme } from '../../styles/theme';
import api from '../../config/api';
import { toast } from 'react-toastify';

const AdminContainer = styled.div`
  min-height: 100vh;
  background: ${theme.colors.background.primary};
  padding: ${theme.spacing.xl};
`;

const AdminHeader = styled.div`
  background: ${theme.colors.background.paper};
  border-radius: ${theme.borderRadius.lg};
  padding: ${theme.spacing.xl};
  margin-bottom: ${theme.spacing.xl};
  box-shadow: ${theme.shadows.medium};
  border: 1px solid ${theme.colors.border.primary};
`;

const AdminTitle = styled.h1`
  color: ${theme.colors.text.primary};
  font-size: 2.5rem;
  font-weight: 700;
  margin: 0 0 ${theme.spacing.sm} 0;
  display: flex;
  align-items: center;
  gap: ${theme.spacing.md};
`;

const AdminSubtitle = styled.p`
  color: ${theme.colors.text.secondary};
  font-size: 1.1rem;
  margin: 0;
`;

const AdminContent = styled.div`
  display: grid;
  gap: ${theme.spacing.xl};
`;

const AdminCard = styled.div`
  background: ${theme.colors.background.paper};
  border-radius: ${theme.borderRadius.lg};
  padding: ${theme.spacing.xl};
  box-shadow: ${theme.shadows.medium};
  border: 1px solid ${theme.colors.border.primary};
`;

const CardTitle = styled.h2`
  color: ${theme.colors.text.primary};
  font-size: 1.5rem;
  font-weight: 600;
  margin: 0 0 ${theme.spacing.lg} 0;
  display: flex;
  align-items: center;
  gap: ${theme.spacing.sm};
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: ${theme.spacing.lg};
  margin-bottom: ${theme.spacing.xl};
`;

const StatCard = styled.div`
  background: ${theme.colors.background.secondary};
  border-radius: ${theme.borderRadius.md};
  padding: ${theme.spacing.lg};
  text-align: center;
  border: 1px solid ${theme.colors.border.secondary};
`;

const StatValue = styled.div`
  font-size: 2rem;
  font-weight: 700;
  color: ${theme.colors.primary.main};
  margin-bottom: ${theme.spacing.xs};
`;

const StatLabel = styled.div`
  font-size: 0.9rem;
  color: ${theme.colors.text.secondary};
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const ToggleRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${theme.spacing.md};
  flex-wrap: wrap;
`;

const ToggleInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const ToggleTitle = styled.div`
  font-weight: 600;
  color: ${theme.colors.text.primary};
`;

const ToggleDescription = styled.div`
  color: ${theme.colors.text.secondary};
  font-size: 0.9rem;
`;

const ToggleLabel = styled.label`
  position: relative;
  display: inline-flex;
  align-items: center;
  cursor: pointer;
  gap: ${theme.spacing.sm};
`;

const ToggleText = styled.span`
  font-size: 0.9rem;
  color: ${theme.colors.text.secondary};
`;

const ToggleSwitch = styled.span`
  position: relative;
  width: 46px;
  height: 26px;
  border-radius: 999px;
  background: ${({ $enabled }) =>
    $enabled ? theme.colors.success.main : theme.colors.background.secondary};
  border: 1px solid ${theme.colors.border.primary};
  transition: background 0.2s ease;
  flex-shrink: 0;
`;

const ToggleThumb = styled.span`
  position: absolute;
  top: 2px;
  left: ${({ $enabled }) => ($enabled ? '22px' : '2px')};
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: ${theme.colors.background.paper};
  box-shadow: 0 1px 3px rgba(0,0,0,0.3);
  transition: left 0.2s ease;
`;

const Admin = () => {
  const { user } = useAuth();
  const [stats, setStats] = useState({
    totalUsers: 0,
    activeUsers: 0,
    newUsersToday: 0,
    superAdmins: 0
  });
  const [dbHealth, setDbHealth] = useState(null);
  const [loadingHealth, setLoadingHealth] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  const [recommendations, setRecommendations] = useState([]);
  const [loadingRecommendations, setLoadingRecommendations] = useState(false);
  const [processingAction, setProcessingAction] = useState(null);
  const [registrationEnabled, setRegistrationEnabled] = useState(null);
  const [savingRegistration, setSavingRegistration] = useState(false);

  useEffect(() => {
    // Fetch admin stats
    fetchAdminStats();
    fetchDatabaseHealth();
    fetchRegistrationSetting();
  // eslint-disable-next-line react-hooks/exhaustive-deps -- run once on mount
  }, []);

  const fetchAdminStats = async () => {
    try {
      const response = await api.get('/api/admin/stats');
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching admin stats:', error);
    }
  };

  const fetchDatabaseHealth = async () => {
    setLoadingHealth(true);
    try {
      const response = await api.get('/api/database-health');
      setDbHealth(response.data);
      fetchRecommendations();
    } catch (error) {
      console.error('Error fetching database health:', error);
    } finally {
      setLoadingHealth(false);
    }
  };

  const fetchRecommendations = async () => {
    setLoadingRecommendations(true);
    try {
      const response = await api.get('/api/database-health/recommendations');
      setRecommendations(response.data.recommendations || []);
    } catch (error) {
      console.error('Error fetching recommendations:', error);
    } finally {
      setLoadingRecommendations(false);
    }
  };

  const fetchRegistrationSetting = async () => {
    try {
      const res = await api.get('/api/admin/settings/registration');
      setRegistrationEnabled(!!res.data?.enabled);
    } catch (error) {
      setRegistrationEnabled(null);
    }
  };

  const saveRegistrationSetting = async (enabled) => {
    setSavingRegistration(true);
    try {
      const res = await api.put('/api/admin/settings/registration', { enabled: !!enabled });
      setRegistrationEnabled(!!res.data?.enabled);
      toast.success(`Account creation ${res.data?.enabled ? 'enabled' : 'disabled'}`);
    } catch (error) {
      toast.error('Failed to update account creation setting');
    } finally {
      setSavingRegistration(false);
    }
  };

  const handleAction = async (action, params) => {
    const actionKey = `${action}-${params?.table || params?.daysToKeep || 'default'}`;
    
    // Prevent duplicate actions
    if (processingAction) {
      toast.info('An action is already in progress...');
      return;
    }
    
    setProcessingAction(actionKey);
    
    try {
      console.log('Handling action:', action, 'with params:', params);
      
      if (action === 'vacuum') {
        if (!params || !params.table) {
          toast.error('Invalid parameters: table name is required');
          setProcessingAction(null);
          return;
        }
        // VACUUM operations can take a long time, use extended timeout (120 seconds)
        const response = await api.post(`/api/database-health/vacuum/${params.table}`, {}, {
          timeout: 120000
        });
        toast.success(response.data?.message || `Vacuumed ${params.table} successfully`);
      } else if (action === 'cleanup-old-data') {
        // Cleanup operations can take a long time, use extended timeout (120 seconds)
        const response = await api.post('/api/database-health/cleanup-old-data', params || {}, {
          timeout: 120000
        });
        const deletedCount = response.data?.deletedCount || 0;
        toast.success(response.data?.message || `Cleaned up ${deletedCount} old records successfully`);
      } else if (action === 'cleanup-orphaned-widgets') {
        // Handle orphaned widgets cleanup if endpoint exists
        toast.info('Orphaned widgets cleanup not yet implemented');
        setProcessingAction(null);
        return;
      } else {
        toast.error(`Unknown action: ${action}`);
        setProcessingAction(null);
        return;
      }
      
      // Refresh health data after successful action
      await fetchDatabaseHealth();
      await fetchRecommendations();
    } catch (error) {
      // Comprehensive error logging
      console.error('❌ Error performing action:', {
        action,
        params,
        error: error.message,
        status: error.response?.status,
        statusText: error.response?.statusText,
        responseData: error.response?.data,
        fullError: error
      });
      
      // Extract all error fields from backend response
      const errorData = error.response?.data || {};
      const errorMessage = errorData.error || errorData.details || error.message || 'Failed to perform action';
      const errorDetail = errorData.detail || '';
      const errorCode = errorData.code || '';
      const errorHint = errorData.hint || '';
      
      // Construct comprehensive error message
      let fullErrorMessage = `Failed: ${errorMessage}`;
      if (errorDetail) {
        fullErrorMessage += `\nDetails: ${errorDetail}`;
      }
      if (errorCode) {
        fullErrorMessage += `\nCode: ${errorCode}`;
      }
      if (errorHint) {
        fullErrorMessage += `\nHint: ${errorHint}`;
      }
      
      // Log the full error message for debugging
      console.error('Full error message:', fullErrorMessage);
      
      // Display error (toast may not support multi-line, so show main message + hint if available)
      const displayMessage = errorHint 
        ? `${errorMessage}\n\n${errorHint}` 
        : errorMessage;
      toast.error(displayMessage, {
        autoClose: errorHint ? 8000 : 5000 // Longer display if there's a hint
      });
    } finally {
      setProcessingAction(null);
    }
  };

  if (!user || user.role !== 'super_admin') {
    return (
      <AdminContainer>
        <AdminHeader>
          <AdminTitle>Access Denied</AdminTitle>
          <AdminSubtitle>You don't have permission to access the admin panel.</AdminSubtitle>
        </AdminHeader>
      </AdminContainer>
    );
  }

  return (
    <AdminContainer>
      <AdminHeader>
        <AdminTitle>
          🛡️ Admin Panel
        </AdminTitle>
        <AdminSubtitle>
          Manage users, system settings, and platform administration
        </AdminSubtitle>
      </AdminHeader>

      <AdminContent>
        <AdminCard>
          <CardTitle>🔐 Platform Settings</CardTitle>
          <ToggleRow>
            <ToggleInfo>
              <ToggleTitle>Account creation</ToggleTitle>
              <ToggleDescription>
                Enable or disable new account creation from the Register page and API.
              </ToggleDescription>
            </ToggleInfo>
            <ToggleLabel>
              <ToggleText>
                {registrationEnabled === null
                  ? 'Loading…'
                  : registrationEnabled
                    ? 'Account creation enabled'
                    : 'Account creation disabled'}
              </ToggleText>
              <ToggleSwitch
                $enabled={!!registrationEnabled}
                onClick={() => {
                  if (savingRegistration || registrationEnabled === null) return;
                  saveRegistrationSetting(!registrationEnabled);
                }}
              >
                <ToggleThumb $enabled={!!registrationEnabled} />
              </ToggleSwitch>
            </ToggleLabel>
          </ToggleRow>
        </AdminCard>

        <AdminCard>
          <CardTitle>
            📊 Database Health & Statistics
          </CardTitle>
          
          {/* Tab Navigation */}
          <div style={{display: 'flex', gap: '1rem', marginBottom: '1.5rem', borderBottom: `2px solid ${theme.colors.border.primary}`}}>
            {['overview', 'recommendations', 'tables'].map(tab => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                style={{
                  padding: '0.75rem 1.5rem',
                  background: 'none',
                  border: 'none',
                  borderBottom: activeTab === tab ? `3px solid ${theme.colors.primary.main}` : '3px solid transparent',
                  color: activeTab === tab ? theme.colors.primary.main : theme.colors.text.secondary,
                  cursor: 'pointer',
                  fontSize: '1rem',
                  fontWeight: activeTab === tab ? 600 : 400,
                  textTransform: 'capitalize',
                  transition: 'all 0.2s'
                }}
              >
                {tab}
              </button>
            ))}
          </div>

          {loadingHealth ? (
            <div style={{textAlign: 'center', padding: '2rem', color: theme.colors.text.secondary}}>
              Loading database health report...
            </div>
          ) : dbHealth ? (
            <div>
              {/* Overview Tab */}
              {activeTab === 'overview' && (
                <StatsGrid>
                  <StatCard>
                    <StatValue style={{fontSize: '1.5rem'}}>{dbHealth.storage?.databaseSize || 'N/A'}</StatValue>
                    <StatLabel>DB Size</StatLabel>
                  </StatCard>
                  <StatCard>
                    <StatValue style={{fontSize: '1.5rem'}}>{dbHealth.connection?.activeConnections || 0}</StatValue>
                    <StatLabel>Connections</StatLabel>
                  </StatCard>
                  <StatCard>
                    <StatValue style={{fontSize: '1.5rem'}}>{dbHealth.connection?.cacheHitRatio || 0}%</StatValue>
                    <StatLabel>Cache Hit</StatLabel>
                  </StatCard>
                  <StatCard>
                    <StatValue style={{fontSize: '1.5rem'}}>{dbHealth.dataIntegrity?.recordCounts?.activeVessels || 0}</StatValue>
                    <StatLabel>Vessels</StatLabel>
                  </StatCard>
                  <StatCard>
                    <StatValue style={{fontSize: '1.5rem'}}>{dbHealth.dataIntegrity?.recordCounts?.activeDashboards || 0}</StatValue>
                    <StatLabel>Dashboards</StatLabel>
                  </StatCard>
                  <StatCard>
                    <StatValue style={{fontSize: '1.5rem'}}>{dbHealth.dataIntegrity?.recordCounts?.vesselDataPoints?.toLocaleString() || 0}</StatValue>
                    <StatLabel>Data Points</StatLabel>
                  </StatCard>
                </StatsGrid>
              )}

              {/* Recommendations Tab */}
              {activeTab === 'recommendations' && (
                <div style={{maxHeight: '500px', overflowY: 'auto'}}>
                  {loadingRecommendations ? (
                    <div style={{textAlign: 'center', padding: '2rem', color: theme.colors.text.secondary}}>
                      Analyzing database...
                    </div>
                  ) : recommendations.length > 0 ? (
                    <div style={{display: 'flex', flexDirection: 'column', gap: '1rem'}}>
                      {recommendations.map((rec, index) => {
                        const actionKey = rec.action ? `${rec.action}-${rec.actionParams?.table || rec.actionParams?.daysToKeep || index}` : null;
                        const isProcessing = processingAction === actionKey;
                        
                        return (
                          <div key={index} style={{
                            padding: '1rem',
                            background: theme.colors.background.secondary,
                            borderLeft: `4px solid ${rec.severity === 'high' ? '#ef4444' : rec.severity === 'medium' ? '#f59e0b' : '#3b82f6'}`,
                            borderRadius: theme.borderRadius.md
                          }}>
                            <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'start', gap: '1rem'}}>
                              <div style={{flex: 1}}>
                                <div style={{fontSize: '0.9rem', fontWeight: 600, color: theme.colors.text.primary, marginBottom: '0.25rem'}}>
                                  {rec.table && `${rec.table}: `}{rec.issue}
                                </div>
                                <div style={{fontSize: '0.85rem', color: theme.colors.text.secondary}}>
                                  {rec.recommendation}
                                </div>
                              </div>
                              {rec.action && (
                                <button
                                  onClick={() => handleAction(rec.action, rec.actionParams)}
                                  disabled={processingAction !== null}
                                  style={{
                                    padding: '0.5rem 1rem',
                                    background: isProcessing ? theme.colors.text.secondary : theme.colors.primary.main,
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: theme.borderRadius.sm,
                                    cursor: processingAction !== null ? 'not-allowed' : 'pointer',
                                    fontSize: '0.85rem',
                                    whiteSpace: 'nowrap',
                                    opacity: processingAction !== null && !isProcessing ? 0.5 : 1,
                                    transition: 'all 0.2s'
                                  }}
                                >
                                  {isProcessing ? 'Processing...' : 'Fix Now'}
                                </button>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div style={{textAlign: 'center', padding: '2rem', color: theme.colors.text.secondary}}>
                      ✅ No issues found - database is healthy!
                    </div>
                  )}
                </div>
              )}

              {/* Tables Tab */}
              {activeTab === 'tables' && (
                <div style={{overflowX: 'auto', maxHeight: '400px', overflowY: 'auto'}}>
                  <table style={{width: '100%', borderCollapse: 'collapse', fontSize: '0.9rem'}}>
                    <thead style={{position: 'sticky', top: 0, background: theme.colors.background.paper}}>
                      <tr style={{borderBottom: `1px solid ${theme.colors.border.primary}`}}>
                        <th style={{padding: '0.5rem', textAlign: 'left', color: theme.colors.text.secondary}}>Table</th>
                        <th style={{padding: '0.5rem', textAlign: 'right', color: theme.colors.text.secondary}}>Size</th>
                        <th style={{padding: '0.5rem', textAlign: 'right', color: theme.colors.text.secondary}}>Rows</th>
                        <th style={{padding: '0.5rem', textAlign: 'right', color: theme.colors.text.secondary}}>Dead</th>
                      </tr>
                    </thead>
                    <tbody>
                      {Array.isArray(dbHealth.tables) ? dbHealth.tables.slice(0, 15).map((table, index) => (
                        <tr key={index} style={{borderBottom: `1px solid ${theme.colors.border.secondary}`}}>
                          <td style={{padding: '0.5rem', color: theme.colors.text.primary}}>{table.table}</td>
                          <td style={{padding: '0.5rem', textAlign: 'right', color: theme.colors.text.primary}}>{table.totalSize}</td>
                          <td style={{padding: '0.5rem', textAlign: 'right', color: theme.colors.text.primary}}>{table.liveRows?.toLocaleString()}</td>
                          <td style={{padding: '0.5rem', textAlign: 'right', color: table.deadRows > 1000 ? '#ef4444' : theme.colors.text.secondary}}>
                            {table.deadRows?.toLocaleString()}
                          </td>
                        </tr>
                      )) : (
                        <tr><td colSpan="4" style={{padding: '0.5rem', textAlign: 'center', color: theme.colors.text.secondary}}>No data</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              )}

              {/* Refresh Button - show on all tabs */}
              <div style={{textAlign: 'center', marginTop: '2rem'}}>
                <button
                  onClick={fetchDatabaseHealth}
                  disabled={loadingHealth}
                  style={{
                    padding: '0.75rem 1.5rem',
                    background: theme.colors.primary.main,
                    color: 'white',
                    border: 'none',
                    borderRadius: theme.borderRadius.md,
                    cursor: 'pointer',
                    fontSize: '1rem',
                    fontWeight: 600
                  }}
                >
                  🔄 Refresh Health Report
                </button>
              </div>
            </div>
          ) : null}
        </AdminCard>

        <AdminCard>
          <CardTitle>
            👥 User Management
          </CardTitle>
          <StatsGrid style={{ marginBottom: '1.5rem' }}>
            <StatCard>
              <StatValue>{stats.totalUsers}</StatValue>
              <StatLabel>Total Users</StatLabel>
            </StatCard>
            <StatCard>
              <StatValue>{stats.activeUsers}</StatValue>
              <StatLabel>Active Users</StatLabel>
            </StatCard>
            <StatCard>
              <StatValue>{stats.newUsersToday}</StatValue>
              <StatLabel>New Today</StatLabel>
            </StatCard>
            <StatCard>
              <StatValue>{stats.superAdmins}</StatValue>
              <StatLabel>Super Admins</StatLabel>
            </StatCard>
          </StatsGrid>
          <UserManagement />
        </AdminCard>

        <AdminCard>
          <CardTitle>
            🧠 AI Knowledge
          </CardTitle>
          <p style={{ fontSize: '0.9rem', color: theme.colors.text.secondary, marginBottom: '1rem' }}>
            Train the AI Assistant on marine electronics manuals, specs, and guides (PDF, Word, URLs). Answers use this knowledge when relevant.
          </p>
          <AIKnowledgeAdmin />
        </AdminCard>

        <AdminCard>
          <CardTitle>
            🧪 PGN Demo
          </CardTitle>
          <PGNDemoAdmin />
        </AdminCard>
      </AdminContent>
    </AdminContainer>
  );
};

export default Admin;
